import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api, { getApiErrorMessage } from '../api';
import EmbedPanel from './EmbedPanel';
import CopyButton from './CopyButton';
import { formatRecent } from '../utils/dates';
import './PollCard.css';

const EDIT_LOCKED_MESSAGE = 'Для редактирования опросов необходимо перейти на другой тариф';
const REPORT_LOCKED_MESSAGE = 'Отчёт с ИИ-резюме доступен на расширенном и корпоративном тарифах';
const EMBED_LOCKED_MESSAGE = 'Встраивание результатов на сайт и в LMS доступно на расширенном и корпоративном тарифах';
// Ширина ряда кнопок, ниже которой карточка переходит в компактную сетку, и сколько кнопок в ней видно
const COMPACT_WIDTH = 560;
const LOCKED_MENU_NOTE = 'Доступно на расширенном и корпоративном тарифах';
const COMPACT_VISIBLE_ACTIONS = 6;
const LOCK_HINT_TOUCH_TIMEOUT_MS = 3000;

const Ico = {
    screen: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <rect x="3" y="4" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    report: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M14 2v6h6M8 18v-3M12 18v-6M16 18v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    power: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M12 3v9M8.5 5.09a7 7 0 109 9.82 7 7 0 00-9-9.82" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
    ),
    qr: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 15h6v6H3v-6zm2 2v2h2v-2H5zm13-7h2v2h-2v-2zm2 4v6h-4v-2h2v-4h2zm-6 4h2v4h-4v-2h2v-2zm-4 0v2H9v-2h4z" />
        </svg>
    ),
    link: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    globe: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" stroke="currentColor" strokeWidth="2" />
        </svg>
    ),
    code: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    pencil: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    sheet: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    brush: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M9.06 11.9l8.97-8.97a2.1 2.1 0 013 3l-8.95 8.95M7 13l2 2M4 21c2-4 5-10 10-15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    lock: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M8 11V7a4 4 0 018 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    trash: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    archive: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <rect x="3" y="4" width="18" height="5" rx="1" stroke="currentColor" strokeWidth="2" />
            <path d="M5 9v10a1 1 0 001 1h12a1 1 0 001-1V9M10 13h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    restore: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M4 12a8 8 0 108-8 8 8 0 00-6.3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M4 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    more: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
        </svg>
    ),
};

const PollCard = ({
    poll,
    onUpdate,
    onDelete,
    onEdit,
    userTier,
    userRole,
    userEmail,
    hasCorporateAccess,
    showActivationHint = false,
    tourAnchor = false,
}) => {
    // Атрибуты для пошагового обучения ставим только на первую карточку.
    const tour = (name) => (tourAnchor ? { 'data-tour': name } : {});
    const showCorpPollActions =
        Boolean(hasCorporateAccess) ||
        userTier === 'corporate' ||
        userRole === 'admin';
    const [showQR, setShowQR] = useState(false);
    const [showLinks, setShowLinks] = useState(false);
    const [showEmbed, setShowEmbed] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const moreMenuRef = useRef(null);
    const actionsRef = useRef(null);
    const measureRef = useRef(null);
    // Сколько кнопок помещается в ряд (null — ещё не измерено, показываем все)
    const [visibleCount, setVisibleCount] = useState(null);
    // Узкая карточка (телефон): кнопки сеткой в две колонки, «Ещё» открывается панелью снизу
    const [compact, setCompact] = useState(false);
    const [loading, setLoading] = useState(false);
    const [studentBotUsername, setStudentBotUsername] = useState('student_feedback_bot');
    const hasNoQuestions = !poll.questions || poll.questions.length === 0;
    // В архиве: ответы не принимаются, доступны результаты, отчёт, выгрузка и возврат из архива
    const archived = Boolean(poll.archived_at);
    // На бесплатном тарифе редактирование закрыто, но пустой опрос можно наполнить вопросами.
    const editLocked = userTier === 'free' && !hasNoQuestions;
    // Отчёт и вопросы из зала — только на платных тарифах
    const paidFeatureLocked = userTier !== 'advanced' && userTier !== 'corporate';
    // Выгрузка в Excel — только корпоративный тариф (роль admin доступа не даёт), как и на backend.
    const canExportExcel = userTier === 'corporate';
    const [showReportConfirm, setShowReportConfirm] = useState(false);
    const [reportNotice, setReportNotice] = useState(null);
    // Подсказка для заблокированной кнопки: native title у disabled-кнопки
    // не показывается во всех браузерах и не работает на тач-устройствах.
    const [lockHint, setLockHint] = useState(null);
    const lockHintTimerRef = useRef(null);
    const lockHintId = `poll-card-lock-hint-${poll.id}`;

    const hideLockHint = () => {
        clearTimeout(lockHintTimerRef.current);
        setLockHint(null);
    };

    const showLockHint = (target, text, autoHide) => {
        clearTimeout(lockHintTimerRef.current);
        const rect = target.getBoundingClientRect();
        const placeBelow = rect.top < 72;
        setLockHint({
            text,
            left: Math.min(Math.max(rect.left + rect.width / 2, 136), window.innerWidth - 136),
            top: placeBelow ? rect.bottom + 8 : rect.top - 8,
            placeBelow,
        });
        if (autoHide) {
            lockHintTimerRef.current = setTimeout(() => setLockHint(null), LOCK_HINT_TOUCH_TIMEOUT_MS);
        }
    };

    useEffect(() => {
        if (!lockHint) return;
        const hide = () => {
            clearTimeout(lockHintTimerRef.current);
            setLockHint(null);
        };
        document.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        return () => {
            document.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [lockHint]);

    useEffect(() => () => clearTimeout(lockHintTimerRef.current), []);

    // Подбираем, сколько кнопок поместится в ряд рядом с «Ещё»; пересчитываем при изменении ширины.
    const actionsSignature = [
        poll.is_active, editLocked, paidFeatureLocked, canExportExcel, showCorpPollActions, archived,
    ].join('|');
    useLayoutEffect(() => {
        const container = actionsRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return undefined;

        const recalc = () => {
            if (container.clientWidth < COMPACT_WIDTH) {
                setCompact(true);
                setVisibleCount(COMPACT_VISIBLE_ACTIONS);
                return;
            }
            setCompact(false);
            const items = Array.from(measure.children);
            const more = items.find((el) => el.dataset.key === '__more');
            const buttons = items.filter((el) => el !== more);
            const gap = parseFloat(getComputedStyle(measure).columnGap) || 0;
            const available = container.clientWidth - (more ? more.offsetWidth : 0) - gap;
            let used = 0;
            let count = 0;
            for (const el of buttons) {
                const next = used + (count > 0 ? gap : 0) + el.offsetWidth;
                if (next > available) break;
                used = next;
                count += 1;
            }
            // Главное действие (активация) показываем всегда
            setVisibleCount(Math.max(1, count));
        };

        recalc();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(recalc);
        observer.observe(container);
        return () => observer.disconnect();
    }, [actionsSignature]);

    // Кнопка, недоступная на тарифе: вместо disabled показывает подсказку при наведении, нажатии и фокусе.
    const lockableButtonProps = (locked, message, onClick) => (locked ? {
        onClick: (e) => showLockHint(e.currentTarget, message, e.nativeEvent.pointerType !== 'mouse'),
        onPointerEnter: (e) => { if (e.pointerType === 'mouse') showLockHint(e.currentTarget, message, false); },
        onPointerLeave: (e) => { if (e.pointerType === 'mouse') hideLockHint(); },
        onFocus: (e) => showLockHint(e.currentTarget, message, false),
        onBlur: hideLockHint,
        'aria-disabled': true,
        'aria-describedby': lockHint && lockHint.text === message ? lockHintId : undefined,
    } : {
        onClick,
        disabled: loading,
    });

    const handleRequestReport = async () => {
        setLoading(true);
        try {
            const res = await api.post(`/polls/${poll.id}/report`);
            setShowReportConfirm(false);
            setReportNotice({
                type: 'success',
                text: `Опрос завершён. Отчёт формируется и в течение нескольких минут придёт на ${res.data.email}.`,
            });
            if (poll.is_active) onUpdate({ ...poll, is_active: false });
        } catch (err) {
            setShowReportConfirm(false);
            setReportNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось запросить отчёт') });
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        api.get('/auth/config').then(res => {
            if (res.data.student_bot_username) {
                setStudentBotUsername(res.data.student_bot_username);
            }
        }).catch(() => { });
    }, []);

    useEffect(() => {
        if (!moreMenuOpen) return;
        const onDocMouseDown = (e) => {
            if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
                setMoreMenuOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setMoreMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [moreMenuOpen]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleToggleActive = async () => {
        setLoading(true);
        try {
            const updated = await api.patch(`/polls/${poll.id}`, {
                is_active: !poll.is_active
            });
            onUpdate(updated.data);
        } catch (err) {
            console.error('Failed to update poll', err);
            const detail = getApiErrorMessage(err, 'Ошибка при обновлении опроса');
            const upgradeNote = err.response?.status === 403 ? '\n\nДля активации и редактирования опросов необходимо перейти на другой тариф.' : '';
            alert(detail + upgradeNote);
        } finally {
            setLoading(false);
        }
    };

    const handleArchive = async () => {
        if (poll.is_active && !window.confirm('Опрос принимает ответы. Остановить приём и отправить опрос в архив?')) return;
        setLoading(true);
        try {
            const res = await api.post(`/polls/${poll.id}/archive`);
            onUpdate(res.data);
        } catch (err) {
            setReportNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось отправить опрос в архив') });
        } finally {
            setLoading(false);
        }
    };

    const handleUnarchive = async () => {
        setLoading(true);
        try {
            const res = await api.post(`/polls/${poll.id}/unarchive`);
            onUpdate(res.data);
        } catch (err) {
            setReportNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось вернуть опрос из архива') });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Вы уверены, что хотите удалить опрос "${poll.title}"? Это действие нельзя отменить!`)) {
            return;
        }
        setLoading(true);
        try {
            await api.delete(`/polls/${poll.id}`);
            onDelete(poll.id);
        } catch (err) {
            console.error('Failed to delete poll', err);
            const detail = getApiErrorMessage(err, 'Ошибка при удалении опроса');
            const upgradeNote = err.response?.status === 403 ? '\n\nДля активации и редактирования опросов необходимо перейти на другой тариф.' : '';
            alert(detail + upgradeNote);
        } finally {
            setLoading(false);
        }
    };

    const handleClearAnswers = async () => {
        setMoreMenuOpen(false);
        if (!window.confirm(`Вы уверены, что хотите удалить ВСЕ ответы для опроса "${poll.title}"? Это действие нельзя отменить!`)) {
            return;
        }
        setLoading(true);
        try {
            await api.post(`/polls/clear-answers/${poll.id}`);
            alert('Все ответы успешно удалены!');
            const res = await api.get(`/polls/${poll.id}`);
            onUpdate({ ...res.data, response_count: 0, answer_count: 0, last_response_at: null });
        } catch (err) {
            console.error('Failed to clear answers', err);
            alert('Ошибка при очистке данных');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFromMenu = () => {
        setMoreMenuOpen(false);
        handleDelete();
    };

    const handleExportExcel = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const apiUrl = api.defaults.baseURL || '';
            const response = await fetch(`${apiUrl}/polls/${poll.id}/export`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Export failed');

            const disposition = response.headers.get('Content-Disposition');
            let filename = `export_${poll.title}.xlsx`;
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export', err);
            alert('Ошибка при выгрузке данных в Excel');
        } finally {
            setLoading(false);
        }
    };

    const pollRef = poll.public_id || String(poll.id);
    const studentLink = `https://t.me/${studentBotUsername}?start=poll_${pollRef}`;
    const webVoteLink = `${window.location.origin}/vote/${pollRef}`;
    const resultsLink = `${window.location.origin}/poll/${pollRef}`;

    const toggleAction = {
        key: 'toggle',
        label: poll.is_active ? 'Деактивировать' : 'Активировать',
        icon: Ico.power,
        // Неактивный опрос: включить — главное действие. Активный: главное — показать результаты
        variant: poll.is_active ? 'danger-text' : 'primary',
        pulse: showActivationHint && !poll.is_active,
        onClick: handleToggleActive,
        disabled: loading,
        tourId: 'poll-activate',
    };

    const reportAction = {
        key: 'report', label: 'Отчёт', icon: Ico.report, variant: 'report',
        onClick: () => { setReportNotice(null); setShowReportConfirm(true); },
        locked: paidFeatureLocked, lockedMessage: REPORT_LOCKED_MESSAGE, tourId: 'poll-report',
    };
    const excelAction = canExportExcel && {
        key: 'excel', label: 'Выгрузить Excel', icon: Ico.sheet, variant: 'excel',
        onClick: handleExportExcel, disabled: loading,
    };

    // Архивный опрос: только то, что нужно для завершённого опроса
    const archivedActions = [
        {
            key: 'results', label: 'Результаты', icon: Ico.screen, variant: 'primary',
            onClick: () => window.open(resultsLink, '_blank', 'noopener'),
            title: 'Открыть результаты в новой вкладке',
        },
        reportAction,
        excelAction,
        {
            key: 'unarchive', label: 'Вернуть из архива', icon: Ico.restore, variant: 'links',
            onClick: handleUnarchive, disabled: loading,
            title: 'Опрос вернётся в список неактивным — его можно будет изменить и запустить снова',
        },
    ].filter(Boolean);

    // Действия карточки в порядке важности: что не помещается в ряд, уходит в меню «Ещё».
    // Цветом выделено одно главное действие, остальные кнопки спокойные.
    const actions = archived ? archivedActions : [
        poll.is_active ? {
            key: 'results', label: 'Результаты', icon: Ico.screen, variant: 'primary',
            onClick: () => window.open(resultsLink, '_blank', 'noopener'),
            title: 'Открыть результаты в новой вкладке — для показа на экране',
        } : toggleAction,
        { key: 'qr', label: 'QR-код', icon: Ico.qr, variant: 'qr', onClick: () => setShowQR((v) => !v), tourId: 'poll-qr' },
        { key: 'links', label: 'Ссылки', icon: Ico.link, variant: 'links', onClick: () => setShowLinks((v) => !v), tourId: 'poll-links' },
        {
            key: 'edit', label: 'Редактировать', shortLabel: 'Изменить', icon: Ico.pencil, variant: 'edit',
            onClick: onEdit, locked: editLocked, lockedMessage: EDIT_LOCKED_MESSAGE, tourId: 'poll-edit',
        },
        poll.is_active && toggleAction,
        reportAction,
        {
            key: 'embed', label: 'Встроить', icon: Ico.code, variant: 'embed',
            onClick: () => setShowEmbed((v) => !v),
            locked: paidFeatureLocked, lockedMessage: EMBED_LOCKED_MESSAGE, tourId: 'poll-embed',
            title: 'Код iframe для сайта, LMS (Moodle) или вебинарной комнаты',
        },
        excelAction,
        showCorpPollActions && {
            key: 'clear', label: 'Очистить данные', icon: Ico.brush, variant: 'clear',
            onClick: handleClearAnswers, disabled: loading, title: 'Удалить все ответы по этому опросу',
        },
    ].filter(Boolean);

    const actionClassName = (action) => [
        'pc-btn',
        `pc-btn--${action.variant}`,
        action.locked ? 'pc-btn--locked' : '',
        action.pulse ? 'pc-btn--pulse' : '',
    ].filter(Boolean).join(' ');

    const visibleActions = actions.slice(0, visibleCount ?? actions.length);
    const hiddenActions = actions.slice(visibleCount ?? actions.length);

    return (
        <div
            className={`poll-card${archived ? ' poll-card--archived' : ''}${showActivationHint ? ' poll-card--highlight-activation' : ''}${moreMenuOpen ? ' poll-card--dropdown-open' : ''}`}
            {...tour('poll-card')}
        >
            <div className="poll-card__top">
                <div className="poll-card__body">
                    <h4 className="poll-card__title">
                        {poll.title}
                    </h4>
                    {poll.description && (
                        <p className="poll-card__desc">
                            {poll.description}
                        </p>
                    )}
                    <div className="poll-card__meta">
                        <span>Вопросов: {poll.questions?.length || 0}</span>
                        <span className="poll-card__meta-sep">·</span>
                        <span>Создан: {formatDate(poll.created_at)}</span>
                        {archived && (
                            <>
                                <span className="poll-card__meta-sep">·</span>
                                <span>В архиве с {formatDate(poll.archived_at)}</span>
                            </>
                        )}
                    </div>
                    {poll.response_count !== null && poll.response_count !== undefined && (
                        <div className="poll-card__activity" title="Респонденты — участники, отправившие опрос. Ответы — ответы на вопросы (несколько вариантов в одном вопросе — один ответ).">
                            {poll.response_count > 0 ? (
                                <>
                                    <span>Респондентов: <b>{poll.response_count.toLocaleString('ru-RU')}</b></span>
                                    <span className="poll-card__meta-sep">·</span>
                                    <span>Ответов: <b>{(poll.answer_count || 0).toLocaleString('ru-RU')}</b></span>
                                    {poll.last_response_at && (
                                        <>
                                            <span className="poll-card__meta-sep">·</span>
                                            <span>Последний ответ: {formatRecent(poll.last_response_at)}</span>
                                        </>
                                    )}
                                </>
                            ) : (
                                <span>Ответов пока нет</span>
                            )}
                        </div>
                    )}
                </div>
                {archived ? (
                    <div className="pc-status pc-status--archived" title="Опрос в архиве: ответы не принимаются, результаты сохранены.">
                        <span className="pc-status__icon">{Ico.archive}</span>
                        В архиве
                    </div>
                ) : (
                <div
                    className={poll.is_active ? 'pc-status pc-status--active' : 'pc-status pc-status--inactive'}
                    title={poll.is_active ? 'Опрос активен. Респонденты могут отвечать.' : 'Опрос не активен. Нажмите кнопку "Активировать", чтобы респонденты могли отвечать на вопросы.'}
                >
                    <span className="pc-status__icon">{poll.is_active ? '✓' : '○'}</span>
                    {poll.is_active ? 'Активен' : 'Не активен'}
                </div>
                )}
            </div>
            {/* Неактивность видна по статусу и кнопке «Активировать»; предупреждаем только о реальной проблеме */}
            {hasNoQuestions && !archived && (
                <div className="poll-card__warning">
                    В опросе нет вопросов — участникам нечего отвечать. Нажмите «Редактировать» и добавьте вопросы.
                </div>
            )}
            {showActivationHint && !poll.is_active && !hasNoQuestions && (
                <div className="poll-card__onboarding-hint">
                    Опрос создан. Следующий шаг — нажмите «Активировать».
                </div>
            )}

            <div className={`poll-card__actions${compact ? ' poll-card__actions--compact' : ''}`} ref={actionsRef}>
                {/* Невидимая копия всех кнопок: по ней меряем ширину, чтобы решить, сколько поместится в ряд */}
                <div className="poll-card__measure" ref={measureRef} aria-hidden="true">
                    {actions.map((action) => (
                        <span key={action.key} className={actionClassName(action)} data-key={action.key}>
                            {action.icon}
                            {action.label}
                        </span>
                    ))}
                    <span className="pc-btn pc-btn--more" data-key="__more">{Ico.more}Ещё</span>
                </div>

                <div className="poll-card__actions-row">
                    {visibleActions.map((action) => (
                        <button
                            key={action.key}
                            type="button"
                            className={actionClassName(action)}
                            {...(action.locked
                                ? lockableButtonProps(true, action.lockedMessage, action.onClick)
                                : { onClick: action.onClick, disabled: action.disabled })}
                            title={action.title}
                            {...tour(action.tourId)}
                            aria-label={compact && action.shortLabel ? action.label : undefined}
                        >
                            {action.icon}
                            {compact && action.shortLabel ? action.shortLabel : action.label}
                        </button>
                    ))}
                </div>

                <div className="poll-card__more" ref={moreMenuRef}>
                    <button
                        type="button"
                        className="pc-btn pc-btn--more"
                        onClick={() => setMoreMenuOpen((o) => !o)}
                        disabled={loading}
                        aria-expanded={moreMenuOpen}
                        aria-haspopup="menu"
                        aria-label={hiddenActions.length ? `Ещё действия: ${hiddenActions.length + 1}` : 'Дополнительные действия'}
                        {...tour('poll-more')}
                    >
                        {Ico.more}
                        Ещё
                        {hiddenActions.length > 0 && <span className="pc-btn__count">{hiddenActions.length}</span>}
                    </button>
                    {moreMenuOpen && compact && (
                        <div className="poll-card__sheet-backdrop" onClick={() => setMoreMenuOpen(false)} aria-hidden="true" />
                    )}
                    {moreMenuOpen && (
                        <ul className={`poll-card__more-menu${compact ? ' poll-card__more-menu--sheet' : ''}`} role="menu">
                            {compact && <li role="none" className="poll-card__sheet-title">{poll.title}</li>}
                            {hiddenActions.map((action) => (
                                <li role="none" key={action.key}>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className={`poll-card__more-item${action.locked ? ' poll-card__more-item--locked' : ''}`}
                                        onClick={() => {
                                            if (action.locked) return;
                                            setMoreMenuOpen(false);
                                            action.onClick();
                                        }}
                                        disabled={!action.locked && action.disabled}
                                        aria-disabled={action.locked || undefined}
                                    >
                                        <span className={`poll-card__more-icon poll-card__more-icon--${action.key}`}>
                                            {action.locked ? Ico.lock : action.icon}
                                        </span>
                                        <span className="poll-card__more-label">
                                            {action.label}
                                            {action.locked && <span className="poll-card__more-note">{LOCKED_MENU_NOTE}</span>}
                                        </span>
                                    </button>
                                </li>
                            ))}
                            {hiddenActions.length > 0 && <li role="separator" className="poll-card__more-sep" />}
                            {!archived && (
                                <li role="none">
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="poll-card__more-item"
                                        onClick={() => { setMoreMenuOpen(false); handleArchive(); }}
                                        disabled={loading}
                                        title="Опрос перестанет принимать ответы и уйдёт из списка. Результаты сохранятся."
                                    >
                                        <span className="poll-card__more-icon">{Ico.archive}</span>
                                        <span className="poll-card__more-label">
                                            В архив
                                            <span className="poll-card__more-note">Результаты сохранятся, опрос можно вернуть</span>
                                        </span>
                                    </button>
                                </li>
                            )}
                            <li role="none">
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="poll-card__more-item poll-card__more-item--delete"
                                    onClick={handleDeleteFromMenu}
                                    disabled={loading}
                                >
                                    <span className="poll-card__more-icon">{Ico.trash}</span>
                                    <span className="poll-card__more-label">Удалить опрос</span>
                                </button>
                            </li>
                        </ul>
                    )}
                </div>
            </div>

            {showQR && (
                <div className="poll-card__panel">
                    <h5 className="poll-card__panel-title">QR-коды для участия</h5>
                    {!poll.is_active && (
                        <div className="poll-card__panel-warning">
                            Пока опрос не активирован, ответы по этим QR-кодам не принимаются.
                        </div>
                    )}

                    <div className="poll-card__qr-grid">
                        <div className="poll-card__qr-col">
                            <div className="poll-card__qr-label">Голосование в Telegram</div>
                            <div className="poll-card__qr-frame">
                                <QRCodeSVG id={`qr-tg-${poll.id}`} value={studentLink} size={200} level="M" includeMargin={true} />
                            </div>
                            <button
                                type="button"
                                className="pc-btn"
                                style={{ marginTop: '0.65rem' }}
                                onClick={() => {
                                    const svg = document.getElementById(`qr-tg-${poll.id}`);
                                    if (!svg) return;
                                    const svgData = new XMLSerializer().serializeToString(svg);
                                    const canvas = document.createElement("canvas");
                                    const ctx = canvas.getContext("2d");
                                    const img = new Image();
                                    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
                                    img.onload = () => {
                                        canvas.width = img.width;
                                        canvas.height = img.height;
                                        ctx.fillStyle = "white";
                                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                                        ctx.drawImage(img, 0, 0);
                                        const pngFile = canvas.toDataURL("image/png");
                                        const downloadLink = document.createElement("a");
                                        downloadLink.download = `poll_telegram_${poll.id}.png`;
                                        downloadLink.href = pngFile;
                                        downloadLink.click();
                                    };
                                }}
                            >
                                Скачать Telegram QR
                            </button>
                        </div>

                        <div className="poll-card__qr-col">
                            <div className="poll-card__qr-label">Голосование на сайте</div>
                            <div className="poll-card__qr-frame">
                                <QRCodeSVG id={`qr-web-${poll.id}`} value={`${window.location.origin}/vote/${pollRef}`} size={200} level="M" includeMargin={true} />
                            </div>
                            <button
                                type="button"
                                className="pc-btn pc-btn--primary"
                                style={{ marginTop: '0.65rem' }}
                                onClick={() => {
                                    const svg = document.getElementById(`qr-web-${poll.id}`);
                                    if (!svg) return;
                                    const svgData = new XMLSerializer().serializeToString(svg);
                                    const canvas = document.createElement("canvas");
                                    const ctx = canvas.getContext("2d");
                                    const img = new Image();
                                    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
                                    img.onload = () => {
                                        canvas.width = img.width;
                                        canvas.height = img.height;
                                        ctx.fillStyle = "white";
                                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                                        ctx.drawImage(img, 0, 0);
                                        const pngFile = canvas.toDataURL("image/png");
                                        const downloadLink = document.createElement("a");
                                        downloadLink.download = `poll_web_${poll.id}.png`;
                                        downloadLink.href = pngFile;
                                        downloadLink.click();
                                    };
                                }}
                            >
                                Скачать Web QR
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showLinks && (
                <div className="poll-card__panel">
                    <h5 className="poll-card__panel-title">Ссылки на опрос</h5>
                    {!poll.is_active && (
                        <div className="poll-card__panel-warning">
                            Пока опрос не активирован, ответы по этим ссылкам не принимаются.
                        </div>
                    )}
                    <div className="poll-card__link-block">
                        <label className="poll-card__link-label">
                            Ссылка для респондентов Telegram
                        </label>
                        <div className="poll-card__link-row">
                            <input
                                type="text"
                                className="poll-card__input"
                                value={studentLink}
                                readOnly
                            />
                            <CopyButton text={studentLink} />
                        </div>
                    </div>
                    <div className="poll-card__link-block">
                        <label className="poll-card__link-label">
                            Ссылка для респондентов Web
                        </label>
                        <div className="poll-card__link-row">
                            <input
                                type="text"
                                className="poll-card__input"
                                value={webVoteLink}
                                readOnly
                            />
                            <CopyButton text={webVoteLink} />
                        </div>
                    </div>
                    <div className="poll-card__link-block">
                        <label className="poll-card__link-label">
                            Ссылка на результаты
                        </label>
                        <div className="poll-card__link-row">
                            <input
                                type="text"
                                className="poll-card__input"
                                value={resultsLink}
                                readOnly
                            />
                            <CopyButton text={resultsLink} />
                        </div>
                    </div>
                </div>
            )}

            {showEmbed && !paidFeatureLocked && <EmbedPanel poll={poll} pollRef={pollRef} />}

            {reportNotice && (
                <div className={`poll-card__report-notice poll-card__report-notice--${reportNotice.type}`} role="status">
                    <span>{reportNotice.text}</span>
                    <button type="button" className="poll-card__report-notice-close" onClick={() => setReportNotice(null)} aria-label="Скрыть">✕</button>
                </div>
            )}

            {showReportConfirm && (
                <div className="app-modal-overlay" role="presentation" onClick={() => !loading && setShowReportConfirm(false)}>
                    <div className="app-modal" role="dialog" aria-modal="true" aria-labelledby={`report-title-${poll.id}`} onClick={(e) => e.stopPropagation()}>
                        <div className="app-modal__header">
                            <h2 id={`report-title-${poll.id}`}>{poll.is_active ? 'Завершить опрос?' : 'Получить отчёт?'}</h2>
                            <button type="button" className="app-modal__close" onClick={() => setShowReportConfirm(false)} disabled={loading} aria-label="Закрыть">✕</button>
                        </div>
                        <div className="app-modal__body">
                            <p className="poll-card__report-text">
                                {poll.is_active
                                    ? <>Опрос «{poll.title}» будет деактивирован — новые ответы и вопросы из зала приниматься не будут. Вы сможете снова активировать его позже.</>
                                    : <>Опрос «{poll.title}» уже завершён.</>}
                            </p>
                            <p className="poll-card__report-text">
                                Мы подготовим отчёт по результатам: ключевые цифры по каждому вопросу, вопросы из зала,
                                резюме, выводы и рекомендации от ИИ. Отчёт придёт на {userEmail ? <strong>{userEmail}</strong> : 'email из профиля'} в течение нескольких минут.
                            </p>
                        </div>
                        <div className="app-modal__footer">
                            <button type="button" className="btn btn--secondary" onClick={() => setShowReportConfirm(false)} disabled={loading}>
                                Отмена
                            </button>
                            <button type="button" className="btn btn--primary" onClick={handleRequestReport} disabled={loading}>
                                {loading ? 'Отправляем…' : (poll.is_active ? 'Завершить и получить отчёт' : 'Получить отчёт')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {lockHint && (
                <div
                    id={lockHintId}
                    role="tooltip"
                    className={`pc-lock-hint${lockHint.placeBelow ? ' pc-lock-hint--below' : ''}`}
                    style={{ left: lockHint.left, top: lockHint.top }}
                >
                    {lockHint.text}
                </div>
            )}
        </div>
    );
};

export default PollCard;
